import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import JSZip from "jszip";
import { Heart, LogOut, Download, Lock } from "lucide-react";
import { clientApi, formatErr, fmtDate, imgUrl, clientImg, typeLabel } from "@/lib/api";
import { useNoReferrer } from "@/lib/useNoReferrer";
import { Lightbox } from "@/components/site/Lightbox";
import { Block, AlbumStatus, Questionnaire } from "./ClientSections";

const saveBlob = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); };
const fileName = (p, k) => `santarelli-${String(k + 1).padStart(3, "0")}.${(p.split(".").pop() || "jpg").slice(0, 4)}`;

const Gallery = ({ ev, setEv, reload }) => {
  const [idx, setIdx] = useState(null);
  const [onlyFav, setOnlyFav] = useState(false);
  const [zipping, setZipping] = useState(false);
  const favs = ev.favorites || [];
  const photos = onlyFav ? ev.gallery_photos.filter((p) => favs.includes(p)) : ev.gallery_photos;
  const toggle = async (photo) => {
    try { const { data } = await clientApi.post("/client/favorites", { photo }); setEv({ ...ev, favorites: data.favorites }); } catch (err) { toast.error(formatErr(err)); }
  };
  const confirm = async () => {
    if (!window.confirm(`Inviare allo studio la selezione di ${favs.length} foto? Dopo l'invio non sarà più modificabile.`)) return;
    try { await clientApi.post("/client/favorites/confirm"); toast.success("Selezione inviata allo studio"); reload(); } catch (err) { toast.error(formatErr(err)); }
  };
  const downloadOne = async (p, k) => { const r = await fetch(clientImg(p), { credentials: "include" }); saveBlob(await r.blob(), fileName(p, k)); };
  const downloadAll = async () => {
    setZipping(true);
    try {
      const zip = new JSZip();
      await Promise.all(ev.gallery_photos.map(async (p, k) => zip.file(fileName(p, k), await (await fetch(clientImg(p), { credentials: "include" })).blob())));
      saveBlob(await zip.generateAsync({ type: "blob" }), `${ev.title.replace(/[^\w]+/g, "-")}.zip`);
    } catch { toast.error("Download non riuscito, riprovate."); }
    setZipping(false);
  };
  if (!ev.gallery_photos.length) return <Block title="Galleria" testid="client-gallery-empty"><p className="text-stoneish">La vostra galleria sarà disponibile qui appena pronta.</p></Block>;
  return (
    <Block title="Galleria" testid="client-gallery" aside={<>{ev.gallery_photos.length} fotografie{ev.favorites_limit ? ` · selezionatene fino a ${ev.favorites_limit} per l'album` : ""}</>}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8 text-sm">
        <button onClick={() => setOnlyFav(!onlyFav)} className={`inline-flex items-center gap-2 ${onlyFav ? "text-terracotta" : "text-stoneish"}`} data-testid="client-fav-filter">
          <Heart size={14} fill={onlyFav ? "currentColor" : "none"} /> Preferite ({favs.length}{ev.favorites_limit ? `/${ev.favorites_limit}` : ""})
        </button>
        {ev.favorites_locked ? <span className="inline-flex items-center gap-2 text-sagedark" data-testid="client-fav-locked"><Lock size={14} /> Selezione inviata allo studio</span>
          : favs.length > 0 && <button onClick={confirm} className="link-u text-ink" data-testid="client-fav-confirm">Invia la selezione allo studio</button>}
        {ev.allow_download && <button onClick={downloadAll} disabled={zipping} className="inline-flex items-center gap-2 link-u ml-auto disabled:opacity-50" data-testid="client-download-all"><Download size={14} /> {zipping ? "Preparazione archivio..." : "Scarica tutte (.zip)"}</button>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
        {photos.map((p, k) => (
          <div key={p} className="relative group aspect-square overflow-hidden bg-cream">
            <button onClick={() => setIdx(k)} className="w-full h-full" data-testid={`client-photo-${k}`}><img src={clientImg(p)} alt="" loading="lazy" className="img-fade is-loaded w-full h-full object-cover" /></button>
            <div className="absolute top-2 right-2 flex gap-1.5">
              {ev.allow_download && <button onClick={() => downloadOne(p, ev.gallery_photos.indexOf(p))} aria-label="Scarica" data-testid={`client-download-${k}`} className="w-8 h-8 rounded-full bg-black/30 text-white backdrop-blur-md flex items-center justify-center opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-500"><Download size={14} /></button>}
              {!ev.favorites_locked || favs.includes(p) ? (
                <button onClick={() => !ev.favorites_locked && toggle(p)} data-testid={`client-fav-btn-${k}`} aria-label="Preferita"
                  className={`w-8 h-8 rounded-full flex items-center justify-center backdrop-blur-md transition-colors duration-500 ${favs.includes(p) ? "bg-ivory text-terracotta" : "bg-black/30 text-white hover:bg-ivory hover:text-terracotta"}`}>
                  <Heart size={14} fill={favs.includes(p) ? "currentColor" : "none"} />
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <Lightbox photos={photos.map(clientImg)} index={idx} onClose={() => setIdx(null)} onIndex={setIdx} />
    </Block>
  );
};

export default function ClientArea() {
  const [ev, setEv] = useState(null);
  const nav = useNavigate();
  useNoReferrer();
  const load = useCallback(() => clientApi.get("/client/me").then((r) => setEv(r.data)).catch(() => nav("/area-clienti")), [nav]);
  useEffect(() => { load(); }, [load]);
  const logout = async () => { try { await clientApi.post("/client/logout"); } finally { nav("/area-clienti"); } };
  if (!ev) return <div className="min-h-screen" />;
  return (
    <div className="pt-36 pb-28 container-x" data-testid="client-area-page">
      <div className="flex flex-wrap justify-between gap-6 items-end pb-14">
        <div>
          <p className="eyebrow">Area privata · {typeLabel(ev.event_type)}</p>
          <h1 className="mt-5 font-serif font-light text-[48px] sm:text-[72px] leading-none tracking-[-0.025em]" data-testid="client-event-title">{ev.title}</h1>
          <p className="mt-5 text-sm text-stoneish">{[ev.date && fmtDate(ev.date), ev.client_name].filter(Boolean).join(" · ")}</p>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-2 text-sm link-u" data-testid="client-logout-btn"><LogOut size={14} strokeWidth={1.4} /> Esci</button>
      </div>
      <Questionnaire ev={ev} reload={load} />
      <AlbumStatus ev={ev} />
      <Gallery ev={ev} setEv={setEv} reload={load} />
    </div>
  );
}
